<template>
  <div>
    <div
      data-testid="tm-pending-payouts__toolbar"
      class="consolidated-results__toolbar tm-pending-payouts__toolbar"
    >
      <div class="consolidated-results__toolbar-left tm-pending-payouts__toolbar-left">
        <button
          type="button"
          data-testid="tm-pending-payouts__back-btn"
          class="tm-pending-payouts__back-btn"
          @click="$emit('back')"
        >
          <v-icon small class="mr-1">mdi-arrow-left</v-icon>
          <span>Back</span>
        </button>

        <button
          type="button"
          data-testid="tm-pending-payouts__filter-btn"
          class="consolidated-results__action-btn"
          @click="$emit('open-filter')"
        >
          <img src="/img/icons/fbc-icons/filter.svg" alt="Filter" class="consolidated-results__action-icon" />
          <span>Filters</span>
          <span
            v-if="activeFilterCount > 0"
            data-testid="tm-pending-payouts__filter-badge"
            class="consolidated-results__badge"
          >
            {{ activeFilterCount }}
          </span>
        </button>

        <button
          type="button"
          data-testid="tm-pending-payouts__sort-btn"
          class="consolidated-results__action-btn"
          @click="$emit('open-sort')"
        >
          <img src="/img/icons/fbc-icons/sort.svg" alt="Sort" class="consolidated-results__action-icon" />
          <span>Sort</span>
          <span
            v-if="activeSortCount > 0"
            data-testid="tm-pending-payouts__sort-badge"
            class="consolidated-results__badge"
          >
            {{ activeSortCount }}
          </span>
        </button>

        <div class="count-card gap-8" data-testid="tm-pending-payouts__total-amount-card">
          <h6 class="count-card__label font-weight-medium mr-3">Total Amount</h6>
          <h4 class="count-card__value font-weight-medium">{{ formattedTotalAmount }}</h4>
        </div>

        <div class="count-card gap-8" data-testid="tm-pending-payouts__total-records-card">
          <h6 class="count-card__label font-weight-medium mr-3">Total Records</h6>
          <h4 class="count-card__value font-weight-medium">{{ formatNumber(totalCount) }}</h4>
        </div>

        <v-btn
          icon
          small
          title="Refresh data"
          data-testid="tm-pending-payouts__refresh-btn"
          @click="$emit('refresh')"
        >
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <div class="consolidated-results__toolbar-right">
        <DownloadExcel
          class="consolidated-results__download-excel-v2"
          button-text="Generate Excel"
          button-color="white"
          test-id-prefix="tm-pending-payouts__download-excel"
          :download-history="downloadHistory"
          :loading-history="loadingHistory"
          :current-user-id="$store.state.user ? String($store.state.user.id || $store.state.user._id || '') : ''"
          :compact="true"
          :total-count="hasLoadedData ? totalCount : 0"
          :max-export-records="maxExportRecords"
          :disabled="loading"
          @generate="$emit('generate-excel')"
          @refresh="$emit('refresh-download-history')"
          @download="$emit('download-history-file', $event)"
        />

        <div v-if="pageCount > 1" class="consolidated-results__toolbar-divider" />

        <div
          v-if="pageCount > 1"
          data-testid="tm-pending-payouts__pagination"
          class="consolidated-results__pagination-v2"
        >
          <button
            type="button"
            data-testid="tm-pending-payouts__prev-button"
            :disabled="page === 1"
            class="consolidated-results__pagination-v2-btn"
            @click="$emit('page-change', page - 1)"
          >
            <v-icon :size="15">mdi-chevron-left</v-icon>
          </button>
          <span data-testid="tm-pending-payouts__page-info" class="consolidated-results__page-info-v2">
            Page <span class="font-weight-bold">{{ page }}</span> of <span class="font-weight-bold">{{ formatNumber(pageCount) }}</span>
          </span>
          <button
            type="button"
            data-testid="tm-pending-payouts__next-button"
            :disabled="page === pageCount"
            class="consolidated-results__pagination-v2-btn"
            @click="$emit('page-change', page + 1)"
          >
            <v-icon :size="15">mdi-chevron-right</v-icon>
          </button>

          <div data-testid="tm-pending-payouts__page-jump" class="consolidated-results__page-jump-v2">
            <input
              v-model.number="pageJumpInput"
              type="text"
              data-testid="tm-pending-payouts__page-jump-input"
              class="consolidated-results__page-jump-v2-input"
              @keyup.enter="jumpToPage"
            />
            <button
              type="button"
              data-testid="tm-pending-payouts__page-jump-btn"
              class="consolidated-results__page-jump-v2-btn"
              :disabled="!isValidPageJump"
              @click="jumpToPage"
            >
              Go
            </button>
          </div>
        </div>
      </div>
    </div>

    <TMResultsSortDropdown
      v-if="showSortModal"
      test-id-prefix="tm-pending-payouts"
      sort-btn-test-id="tm-pending-payouts__sort-btn"
      :active-sort-tab="activeSortTab"
      :available-sort-columns="availableSortColumns"
      :saved-sort="savedSort"
      :playground-sort="playgroundSort"
      :enable-saved-filter-sort="enableSavedFilterSort"
      @close="$emit('close-sort')"
      @update:active-sort-tab="$emit('update:active-sort-tab', $event)"
      @toggle-sort-column="$emit('toggle-sort-column', $event)"
      @set-sort-direction="(key, direction) => $emit('set-sort-direction', key, direction)"
      @reset-sort="$emit('reset-sort')"
      @apply-sort="$emit('apply-sort')"
    />
  </div>
</template>

<script>
import DownloadExcel from '../TransationMatching/downloadExcel.vue';
import TMResultsSortDropdown from '../shared/TMResultsSortDropdown.vue';
import { formatNumber, MAX_EXCEL_EXPORT_RECORDS } from '../shared/tmResultsDataTableUtils.js';

export default {
  name: 'TMPendingPayoutsToolbar',

  components: {
    DownloadExcel,
    TMResultsSortDropdown,
  },

  props: {
    totalAmount: {
      type: Number,
      default: 0,
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    activeFilterCount: {
      type: Number,
      default: 0,
    },
    activeSortCount: {
      type: Number,
      default: 0,
    },
    downloadHistory: {
      type: Array,
      default: () => [],
    },
    loadingHistory: {
      type: Boolean,
      default: false,
    },
    showSortModal: {
      type: Boolean,
      default: false,
    },
    activeSortTab: {
      type: String,
      default: 'playground',
    },
    availableSortColumns: {
      type: Array,
      default: () => [],
    },
    savedSort: {
      type: Array,
      default: () => [],
    },
    playgroundSort: {
      type: Array,
      default: () => [],
    },
    enableSavedFilterSort: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Number,
      default: 1,
    },
    pageCount: {
      type: Number,
      default: 0,
    },
    hasLoadedData: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      pageJumpInput: null,
      maxExportRecords: MAX_EXCEL_EXPORT_RECORDS,
    };
  },

  computed: {
    formattedTotalAmount() {
      if (this.totalAmount === null || this.totalAmount === undefined) return '–';
      return this.$nova.formatNumber(this.totalAmount);
    },

    isValidPageJump() {
      return (
        this.pageJumpInput !== null
        && this.pageJumpInput >= 1
        && this.pageJumpInput <= this.pageCount
        && this.pageJumpInput !== this.page
      );
    },

  },

  methods: {
    formatNumber(num) {
      return formatNumber(num);
    },

    jumpToPage() {
      if (this.isValidPageJump) {
        this.$emit('page-change', this.pageJumpInput);
        this.pageJumpInput = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './TMPendingPayoutsView.scss';
</style>
